import {
    useEffect,
    useState,
} from "react";

import DataTable from "components/table/DataTable";

import {
    getOrganizations,
    deleteOrganization,
} from "services/api/organizationApi";

import {
    showSuccess,
    showError,
} from "utils/toast";

import {
    organizationColumns,
} from "./columns";

import OrganizationModal from "./OrganizationModal";

import {
    ORGANIZATION_MASTERS,
} from "./constants";


export default function Organization() {

    const [
        master,
        setMaster,
    ] = useState(
        "business-regions"
    );


    const [
        organizations,
        setOrganizations,
    ] = useState([]);


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        search,
        setSearch,
    ] = useState("");


    const [
        page,
        setPage,
    ] = useState(1);


    const [
        pagination,
        setPagination,
    ] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
    });


    const [
        openModal,
        setOpenModal,
    ] = useState(false);


    const [
        selectedItem,
        setSelectedItem,
    ] = useState(null);


    // ============================================================
    // CURRENT MASTER
    // ============================================================

    const currentMaster =
        ORGANIZATION_MASTERS.find(
            item =>
                item.value === master
        );


    // ============================================================
    // LOAD DATA
    // ============================================================

    const loadData = async () => {
        try {
            setLoading(true);

            const response =
                await getOrganizations(
                    master,
                    page,
                    10,
                    search
                );

            console.log(
                "ORGANIZATION RESPONSE:",
                response
            );

            const records =
                response?.data?.items ||
                response?.data?.records ||
                response?.data?.regions ||
                response?.items ||
                response?.records ||
                response?.regions ||
                [];

            const paginationData =
                response?.data?.pagination ||
                response?.pagination ||
                {};

            setOrganizations(
                records
            );

            setPagination(
                paginationData
            );

        } catch (err) {

            console.error(
                "Organization load error:",
                err
            );

            setOrganizations([]);

            showError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to load organization data."
            );

        } finally {
            setLoading(false);
        }
    };


    // ============================================================
    // LOAD WHEN MASTER / PAGE / SEARCH CHANGES
    // ============================================================

    useEffect(() => {

        loadData();

    }, [
        master,
        page,
        search,
    ]);


    // ============================================================
    // MASTER CHANGE
    // ============================================================

    const handleMasterChange = (
        e
    ) => {

        setMaster(
            e.target.value
        );

        setPage(
            1
        );

        setSearch("");

        setSelectedItem(
            null
        );

    };


    // ============================================================
    // ADD
    // ============================================================

    const handleAdd = () => {

        setSelectedItem(
            null
        );

        setOpenModal(
            true
        );

    };


    // ============================================================
    // EDIT
    // ============================================================

    const handleEdit = (
        item
    ) => {

        setSelectedItem(
            item
        );

        setOpenModal(
            true
        );

    };


    // ============================================================
    // DELETE
    // ============================================================

    const handleDelete = async (
        id
    ) => {

        const confirmMessage =
            master === "business-regions"
                ? "Are you sure you want to delete this business region?"
                : "Delete record?";


        if (
            !window.confirm(
                confirmMessage
            )
        ) {
            return;
        }


        try {

            setLoading(true);


            await deleteOrganization(
                master,
                id
            );


            showSuccess(
                master === "business-regions"
                    ? "Business region deleted successfully."
                    : "Deleted successfully."
            );


            await loadData();

        } catch (err) {

            console.error(
                "Delete organization error:",
                err
            );


            showError(
                err?.response?.data?.message ||
                "Unable to delete record."
            );

        } finally {

            setLoading(false);

        }

    };


    // ============================================================
    // MODAL CLOSE
    // ============================================================

    const handleModalClose = () => {

        setOpenModal(
            false
        );

        setSelectedItem(
            null
        );

    };


    // ============================================================
    // RENDER
    // ============================================================

    return (

        <div className="mt-5 space-y-5">


            {/* =====================================================
                HEADER
            ===================================================== */}

            <div
                className="
                    flex
                    flex-col
                    gap-4
                    rounded-2xl
                    bg-white
                    p-5
                    shadow-sm

                    md:flex-row
                    md:items-center
                    md:justify-between
                "
            >

                <div>

                    <h1
                        className="
                            text-2xl
                            font-bold
                            text-navy-700
                        "
                    >
                        Organization Hierarchy
                    </h1>


                    <p
                        className="
                            mt-1
                            text-sm
                            text-gray-500
                        "
                    >
                        Manage global geography,
                        organization masters and
                        operational territories.
                    </p>

                </div>


                {/* =================================================
                    MASTER SELECT
                ================================================= */}

                <select
                    value={
                        master
                    }
                    onChange={
                        handleMasterChange
                    }
                    className="
                        rounded-xl
                        border
                        border-gray-300
                        bg-white
                        px-4
                        py-3
                        text-sm
                        font-medium
                        outline-none
                        focus:border-brand-500
                        focus:ring-1
                        focus:ring-brand-500
                    "
                >

                    {
                        ORGANIZATION_MASTERS.map(
                            item => (

                                <option
                                    key={
                                        item.value
                                    }
                                    value={
                                        item.value
                                    }
                                >
                                    {
                                        item.label
                                    }
                                </option>

                            )
                        )
                    }

                </select>

            </div>


            {/* =====================================================
                TABLE
            ===================================================== */}

            <DataTable

                title={
                    currentMaster?.label ||
                    "Organization"
                }


                columns={
                    organizationColumns({

                        onEdit:
                            handleEdit,

                        onDelete:
                            handleDelete,

                        master:
                            master,

                    })
                }


                data={
                    organizations
                }


                loading={
                    loading
                }


                onAdd={
                    handleAdd
                }


                addButtonText={
                    master ===
                    "business-regions"
                        ? "Add Business Region"
                        : "Add"
                }

            />


            {/* =====================================================
                PAGINATION INFO
            ===================================================== */}

            <div
                className="
                    flex
                    items-center
                    justify-between
                    rounded-xl
                    bg-white
                    px-5
                    py-3
                    text-sm
                    text-gray-500
                    shadow-sm
                "
            >

                <div>

                    Total Records:

                    <span
                        className="
                            ml-1
                            font-semibold
                            text-gray-700
                        "
                    >
                        {
                            pagination?.total ||
                            0
                        }
                    </span>

                </div>


                <div>

                    Page

                    <span
                        className="
                            mx-1
                            font-semibold
                            text-gray-700
                        "
                    >
                        {
                            pagination?.page ||
                            page
                        }
                    </span>

                    of

                    <span
                        className="
                            ml-1
                            font-semibold
                            text-gray-700
                        "
                    >
                        {
                            pagination?.totalPages ||
                            0
                        }
                    </span>

                </div>

            </div>


            {/* =====================================================
                MODAL
            ===================================================== */}

            <OrganizationModal

                open={
                    openModal
                }


                onClose={
                    handleModalClose
                }


                reload={
                    loadData
                }


                master={
                    master
                }


                item={
                    selectedItem
                }

            />

        </div>

    );

}